import { Injectable, inject } from '@angular/core'
import { AddRecordContentComponent } from '@app/feature/add-record/add-record-content.component'
import { PinDraftRecord } from '@app/feature/pin-panel/pin-record.service'
import { ModalFlowManagerService } from '@components-library/tb-modal-manager/modal-flow-manager.service'
import { getBoolean } from '@core/global-util'
import { LogService } from '@services/logger/log.service'
import { RecordCellsService } from '@services/record-cells.service'
import {
    WorkspaceFacadeService,
    SchemaFacadeService,
    FieldFacadeService,
} from '@services/store-facade'
import { reduce, without } from 'lodash-es'
import { EMPTY, combineLatest, Observable } from 'rxjs'
import { map, switchMap, take } from 'rxjs/operators'
import {
    AppRecord,
    BaseMainBusinessRecord,
    MainBusinessRecord,
    Cell,
    CellEntities,
    CreateRecordModel,
    FieldTypes,
    RecordUpdate,
    UpdateLinkModel,
    UpdateResponseModel,
    TableModel,
    ResponseRecord,
    FieldEntities,
} from '../models'
import { UpdateService } from './update.service'
import { Logger } from '@services/logger/logger'

/**
 * Simplified create-record result used by add-record and quick-create flows.
 */
export type CreateRecordObject = {
    status: string
    recordGuid?: string
    record?: ResponseRecord
}

@Logger()
@Injectable({
    providedIn: 'root',
})
/**
 * Coordinates record-level mutations, draft duplication, and favorite toggling.
 */
export class RecordsService {
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly updateService = inject(UpdateService)
    private readonly modalFlowManagerService = inject(ModalFlowManagerService)
    private readonly logService = inject(LogService)
    private readonly recordCellsService = inject(RecordCellsService)

    updateLink(data: RecordUpdate) {
        this.schemaFacadeService.selectSelectedTableSchema$
            .pipe(
                take(1),
                switchMap((schema) => {
                    if (!schema) {
                        this.logService.error(new Error('no schema in updateLink'))
                        return EMPTY
                    }
                    const linkData: UpdateLinkModel = {
                        record_guid: data.record.guid!,
                        record_revision: data.record.revision!,
                        object_field_guid: data.cell!.field_guid,
                        revision: data.cell!.revision === 0 ? 1 : data.cell!.revision,
                        add_guids: data.value?.add_guids ?? [],
                        delete_guids: data.value?.delete_guids ?? [],
                    }
                    return this.updateService.updateLink([linkData], schema)
                }),
            )
            .subscribe()
    }

    toggleFavorite(record: MainBusinessRecord, userGuid: string) {
        return this.updateRecord({
            record,
            cell: record.watch,
            value: this.updateFavorite(record.watch.value, userGuid),
        })
    }

    updateFavorite(value: string, userGuid: string) {
        const watchValue = value.length ? value.split(',') : []

        if (watchValue.includes(userGuid)) {
            return without(watchValue, userGuid).join(',')
        }

        watchValue.push(userGuid)
        return watchValue.join(',')
    }

    isFavorite(value: string, userGuid: string) {
        if (!value) return false

        if (value.includes(',')) {
            return value.split(',').includes(userGuid)
        }

        return value === userGuid
    }

    duplicateRecord(record: BaseMainBusinessRecord) {
        if (!record.workspace_guids) return

        combineLatest([
            this.schemaFacadeService.selectSchemaByGuid$(record.schema_guid),
            this.workspaceFacadeService.selectSelectedWorkspace$,
            this.workspaceFacadeService.selectWorkspacesByGuids$(record.workspace_guids),
            this.fieldFacadeService.selectFieldEntitiesBySchemaGuid$(record.schema_guid),
        ])
            .pipe(take(1))
            .subscribe(([schema, workspace, workspaces, fieldEntities]) => {
                if (!schema) return

                const cells = this.recordCellsService.getRecordCells(record)
                const fieldValues = this.setFieldValues(
                    fieldEntities,
                    cells,
                    record.name.field_guid,
                )

                const data: PinDraftRecord = {
                    name: `${record.name.value} (copy)`,
                    data: {
                        schemaGuid: record.schema_guid,
                        workspaces: workspaces,
                        selectedWorkspace: workspace,
                        name: `${record.name.value} (copy)`,
                        fieldValues,
                    },
                    guid: record.guid,
                }

                this.modalFlowManagerService
                    .openDialog<AddRecordContentComponent, PinDraftRecord>({
                        component: AddRecordContentComponent,
                        data,
                    })
                    .pipe(take(1))
            })
    }

    updateRecord(data: RecordUpdate) {
        return this.updateRecords([data])
    }

    updateRecords(data: RecordUpdate[]) {
        return this.updateService.updateRecords(data)
    }

    createRecord(record: CreateRecordModel): Observable<CreateRecordObject> {
        return this.updateService.createRecord(record).pipe(
            take(1),
            map((data: UpdateResponseModel) => {
                const createResponseObject: CreateRecordObject = {
                    status: data.status,
                }
                if (data.status === 'success') {
                    const recordEntities = (<TableModel>data.data[0].object).added!
                    const recordGuid = Object.keys(recordEntities)[0]
                    createResponseObject.recordGuid = recordGuid
                    createResponseObject.record = recordEntities[recordGuid]
                }
                return createResponseObject
            }),
        )
    }

    deleteRecord(data: AppRecord | Array<AppRecord>) {
        return this.updateService.deleteRecord(data)
    }

    private setFieldValues(
        fieldEntities: FieldEntities,
        cells: CellEntities,
        nameFieldGuid: string,
    ) {
        return reduce(
            cells,
            (acc, cell, guid) => {
                if (getBoolean(fieldEntities[guid].is_readonly)) return acc

                if (cell.field_type === FieldTypes.STATUS) {
                    acc[guid] = this.setStatusFieldValue(fieldEntities, guid, cell)
                    return acc
                }

                acc[guid] = nameFieldGuid === guid ? `${cell.value} (copy)` : cell.value
                return acc
            },
            {} as Record<string, string>,
        )
    }

    private setStatusFieldValue(fieldEntities: FieldEntities, guid: string, cell: Cell) {
        const selectObjects = fieldEntities[guid].select_object_field

        if (!selectObjects) return ''

        const isStatusValueExists = Object.keys(selectObjects).find((optionGuid) => {
            return optionGuid === cell.value
        })

        if (!isStatusValueExists) {
            return fieldEntities[guid].default_value ?? ''
        }

        return cell.value
    }
}
